import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type SetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

export const Route = createFileRoute("/set-password")({
  component: SetPassword,
});

function SetPassword() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState<string>("");
  const [invitationToken, setInvitationToken] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = useForm<SetPasswordFormData>();

  const password = watch("password");

  useEffect(() => {
    const processInvitation = async () => {
      try {
        // Get invitation token from URL
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get("token");

        if (!token) {
          toast.error("Invalid invitation link - missing token");
          navigate({ to: "/login" });
          return;
        }

        setInvitationToken(token);

        // Check if we already have a session (coming from /invite page)
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (session && session.user) {
          console.log("Existing session found");
          setEmail(session.user.email || "");
          setIsLoading(false);
          return;
        }

        // Check if we have access token in hash (from email verification or recovery)
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1),
        );
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const type = hashParams.get("type");

        console.log("=== SET PASSWORD PAGE DEBUG ===");
        console.log("URL:", window.location.href);
        console.log("Hash params:", Object.fromEntries(hashParams.entries()));
        console.log("Access token exists:", !!accessToken);
        console.log("Refresh token exists:", !!refreshToken);
        console.log("Type:", type);

        if (accessToken && refreshToken) {
          // Decode the JWT to get user email
          try {
            const payload = JSON.parse(atob(accessToken.split(".")[1]));
            console.log("JWT payload:", payload);
            console.log("User email from token:", payload.email);

            if (payload.email) {
              setEmail(payload.email);
            }

            // Set the session
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            console.log("Set session result:", { data, error });

            if (error) {
              console.error("Failed to set session:", error);
              toast.error(
                "Failed to authenticate. Please request a new invitation.",
              );
              navigate({ to: "/login" });
              return;
            }

            // Get current user
            const {
              data: { user },
              error: userError,
            } = await supabase.auth.getUser();
            console.log("Current user:", user);
            console.log("User error:", userError);

            if (user) {
              console.log("Authenticated user email:", user.email);
              console.log("User metadata:", user.user_metadata);
              console.log("User confirmed at:", user.confirmed_at);
              console.log("User email confirmed:", user.email_confirmed_at);
            }
          } catch (error) {
            console.error("Error decoding token:", error);
          }
        } else {
          // Check for OTP errors in hash
          const errorCode = hashParams.get("error_code");
          const errorDescription = hashParams.get("error_description");

          if (errorCode === "otp_expired") {
            console.log("OTP expired, user needs to request new invitation");
            toast.error(
              "Your invitation link has expired. Please request a new invitation.",
            );
          } else {
            console.log("No access token in URL hash and no existing session");
            // If coming from /invite, we should have a session
            if (!session) {
              toast.error("Please log in first to set your password");
            }
          }

          if (!session) {
            navigate({ to: "/login" });
            return;
          }
        }
      } catch (error) {
        console.error("Error processing invitation:", error);
        toast.error("Failed to process invitation");
        navigate({ to: "/login" });
      } finally {
        setIsLoading(false);
      }
    };

    processInvitation();
  }, [navigate]);

  const onSubmit = async (data: SetPasswordFormData) => {
    try {
      console.log("=== UPDATING PASSWORD ===");

      // Check current session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      console.log("Current session:", session);
      console.log("Session error:", sessionError);

      if (!session) {
        setError("root", {
          type: "manual",
          message: "No active session. Please request a new invitation.",
        });
        return;
      }

      // Update the user's password and remove needs_password_setup flag
      const { data: updateData, error: updateError } =
        await supabase.auth.updateUser({
          password: data.password,
          data: {
            needs_password_setup: false,
          },
        });

      console.log("Update result:", updateData);
      console.log("Update error:", updateError);

      if (updateError) {
        setError("root", {
          type: "manual",
          message: updateError.message,
        });
        return;
      }

      toast.success("Password updated");

      // Redirect to invite page with token
      navigate({ to: "/invite", search: { token: invitationToken } });
    } catch (error) {
      console.error("Error setting password:", error);
      setError("root", {
        type: "manual",
        message: "An unexpected error occurred",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Processing invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Set your password</CardTitle>
              <CardDescription>
                {email ? (
                  <>Please set a password for your account ({email})</>
                ) : (
                  <>Please set a password to complete your registration</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-3">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      {...register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 6,
                          message: "Password must be at least 6 characters",
                        },
                      })}
                    />
                    {errors.password && (
                      <p className="text-sm text-red-600">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      {...register("confirmPassword", {
                        required: "Please confirm your password",
                        validate: (value) =>
                          value === password || "Passwords do not match",
                      })}
                    />
                    {errors.confirmPassword && (
                      <p className="text-sm text-red-600">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                  {errors.root && (
                    <p className="text-sm text-red-600">
                      {errors.root.message}
                    </p>
                  )}
                  <div className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Setting password..."
                        : "Set Password & Continue"}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

